import { useState } from "react";
import { api } from "@/lib/api";
import { fmtEur, fmtDate, vatOf, cassaOf } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Wallet, X } from "lucide-react";

export const PAY_STATUS = [
  { value: "da_pagare", label: "Da pagare", cls: "bg-red-500/10 text-red-300 border-red-500/25" },
  { value: "acconto", label: "Acconto", cls: "bg-amber-500/10 text-amber-300 border-amber-500/25" },
  { value: "saldato", label: "Saldato", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/25" },
];

export const paymentInfo = (e) => {
  const total = Number(e.revenue || 0) + (cassaOf(e) === "nero" ? 0 : vatOf(e.revenue, e.revenue_vat_rate));
  const paid = (e.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);
  const rest = Math.max(0, total - paid);
  const status = paid <= 0 ? "da_pagare" : (paid + 0.005 >= total ? "saldato" : "acconto");
  return { total, paid, rest, status };
};

export const PaymentStrip = ({ event, onOpen }) => {
  const { total, paid, rest, status } = paymentInfo(event);
  const st = PAY_STATUS.find((s) => s.value === status);
  return (
    <button data-testid={`event-payments-btn-${event.id}`} onClick={() => onOpen(event)} className="mt-3 w-full text-left bg-[#1A2335] hover:bg-[#1F2A40] border border-[#222D42] rounded-lg px-3 py-2 transition-colors">
      <div className="flex items-center justify-between gap-2">
        <span data-testid={`event-pay-status-${event.id}`} className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${st.cls}`}>{st.label}</span>
        <span className="text-[10px] text-slate-500">totale {fmtEur(total)}</span>
      </div>
      <div className="flex items-center justify-between text-xs mt-1.5">
        <span className="text-slate-400">Incassato <span data-testid={`event-paid-${event.id}`} className="font-mono text-emerald-300">{fmtEur(paid)}</span></span>
        <span className="text-slate-400">Resta <span data-testid={`event-rest-${event.id}`} className={`font-mono font-semibold ${rest > 0 ? "text-amber-300" : "text-slate-500"}`}>{fmtEur(rest)}</span></span>
      </div>
    </button>
  );
};

const inp = "bg-[#0d1320] border border-[#222D42] rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-amber-500/50";

const PaymentRow = ({ p, onDelete }) => {
  const [ask, setAsk] = useState(false);
  return (
    <div data-testid={`payment-row-${p.id}`} className="flex items-center justify-between gap-3 bg-[#1A2335] border border-[#222D42] rounded-lg p-3">
      <div className="min-w-0">
        <p className="font-mono text-sm text-emerald-300">{fmtEur(p.amount)}</p>
        <p className="text-[11px] text-slate-500 truncate">{p.date ? fmtDate(p.date) : "senza data"}{p.note ? ` · ${p.note}` : ""}</p>
      </div>
      {ask ? (
        <div className="flex items-center gap-1.5 shrink-0">
          <button data-testid={`payment-delete-confirm-${p.id}`} onClick={() => onDelete(p.id)} className="text-xs bg-red-500 hover:bg-red-400 text-white font-semibold px-2.5 py-1 rounded-md">Elimina</button>
          <button data-testid={`payment-delete-cancel-${p.id}`} onClick={() => setAsk(false)} className="text-xs text-slate-400 px-2 py-1">Annulla</button>
        </div>
      ) : (
        <button data-testid={`payment-delete-${p.id}`} onClick={() => setAsk(true)} className="p-1.5 rounded-md text-red-400 hover:bg-red-500/10 shrink-0"><Trash2 className="w-4 h-4" /></button>
      )}
    </div>
  );
};

export const PaymentsModal = ({ event, onClose, onChange }) => {
  const [f, setF] = useState({ amount: "", date: new Date().toISOString().slice(0, 10), note: "" });
  if (!event) return null;
  const { total, paid, rest } = paymentInfo(event);
  const add = async () => {
    const amount = Number(f.amount);
    if (!(amount > 0)) return toast.error("Inserisci un importo valido");
    try { onChange(await api.addEventPayment(event.id, { amount, date: f.date || null, note: f.note.trim() })); setF({ ...f, amount: "", note: "" }); toast.success("Pagamento registrato"); }
    catch { toast.error("Errore nel salvataggio"); }
  };
  const del = async (pid) => {
    try { onChange(await api.deleteEventPayment(event.id, pid)); toast.success("Pagamento eliminato"); }
    catch { toast.error("Errore"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="payments-modal" className="relative bg-[#121824] border border-[#222D42] rounded-2xl w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-[#222D42]">
          <h3 className="font-head text-lg font-semibold text-slate-100 flex items-center gap-2 min-w-0"><Wallet className="w-4 h-4 text-amber-400 shrink-0" /><span className="truncate">Pagamenti · {event.name}</span></h3>
          <button data-testid="payments-modal-close" onClick={onClose} className="text-slate-400 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-[#1A2335] rounded-lg py-2"><p className="text-[10px] uppercase text-slate-500">Totale</p><p data-testid="payments-total" className="font-mono text-sm text-slate-200">{fmtEur(total)}</p></div>
            <div className="bg-emerald-500/10 rounded-lg py-2"><p className="text-[10px] uppercase text-slate-500">Incassato</p><p data-testid="payments-paid" className="font-mono text-sm text-emerald-300">{fmtEur(paid)}</p></div>
            <div className="bg-amber-500/10 rounded-lg py-2"><p className="text-[10px] uppercase text-slate-500">Resta</p><p data-testid="payments-rest" className="font-mono text-sm font-semibold text-amber-300">{fmtEur(rest)}</p></div>
          </div>
          <p className="text-[11px] text-slate-600 -mt-2">{cassaOf(event) === "nero" ? "Totale senza IVA (incasso in contanti)." : "Totale IVA inclusa."} I pagamenti segnano solo lo stato dell'evento: non creano entrate in Contabilità.</p>
          <div className="space-y-2">
            {(event.payments || []).length === 0 && <p className="text-slate-600 text-sm">Nessun pagamento registrato.</p>}
            {(event.payments || []).map((p) => <PaymentRow key={p.id} p={p} onDelete={del} />)}
          </div>
          <div className="border-t border-[#222D42] pt-4 space-y-2">
            <p className="text-[11px] uppercase tracking-wider text-slate-500">Registra acconto o saldo</p>
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="payment-amount-input" type="number" placeholder="Importo €" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={`${inp} font-mono`} />
              <input data-testid="payment-date-input" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inp} />
            </div>
            <input data-testid="payment-note-input" placeholder="Nota (es. bonifico, contanti)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className={`${inp} w-full`} />
            <div className="flex gap-2">
              {rest > 0 && <button data-testid="payment-fill-rest" onClick={() => setF({ ...f, amount: rest.toFixed(2) })} className="text-xs bg-[#1A2335] border border-[#222D42] text-slate-300 hover:text-amber-300 px-3 py-2 rounded-lg">Saldo {fmtEur(rest)}</button>}
              <button data-testid="payment-add-button" onClick={add} className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform"><Plus className="w-4 h-4" /> Aggiungi pagamento</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
